import React, { useEffect, useCallback, useMemo } from 'react';
import Card from 'react-bootstrap/Card';
import ListGroup from 'react-bootstrap/ListGroup';
import { Button } from '@material-ui/core';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import debug from 'debug';

import {
  getSelectedPos,
  getEmployeePOSByWarehouseIDS,
} from 'reducers/PointsOfSale';
import { selectPos } from 'actions/PointsOfSale';
import { getAllRegisters, getSetting } from 'reducers/configs/settings';
import Loader from 'components/Loader';
import { getIsLoading } from 'reducers/OpenCloseDay';
import {
  getSelectedWarehouse,
  getAllowedWarehouses,
  getIsFetchingWarehouses,
} from 'reducers/warehouses';
import { UrlControl } from 'services/UrlControl/UrlControl';
import { getVatRatesFetching } from 'reducers/vatRatesDB';
import { hardLogout } from 'actions/Login';

const styles = {
  card: {
    width: '358px',
    maxWidth: '100%',
    borderRadius: '8px',
    border: 0,
  },
  header: {
    fontWeight: 700,
    fontSize: '24px',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    height: '66px',
    padding: '15px 15px 15px 22px',
  },
  body: {
    maxHeight: '70vh',
    overflowY: 'scroll',
    padding: '16px 0px 0px',
    marginBottom: '20px',
  },
  close: {
    fontSize: '42px',
  },
  pos: {
    minHeight: '67px',
    padding: '16px 22px',
    textAlign: 'left',
    color: '#555',
    border: 0,
    borderRadius: 0,
  },
};

const baseLog = debug('PosSelect');

const useAutoSelectPos = (onSelect, allOptions, allowedOptions) => {
  const selectedPOS = useSelector(getSelectedPos);
  const associatedPosId = useSelector(
    getSetting('CONFIGURATION: associated-pos'),
  );

  const posFrom = {
    urlControl: allOptions.find(
      pos =>
        Number(pos.pointOfSaleID) === UrlControl.autoSelectPos?.pointOfSaleID,
    ),
    associatedPos: allowedOptions.find(
      pos => String(pos.pointOfSaleID) === String(associatedPosId),
    ),
    alreadySelected: allowedOptions.find(
      pos => String(pos.pointOfSaleID) === String(selectedPOS?.pointOfSaleID),
    ),
  };
  const autoSelect = useMemo(() => {
    const log = baseLog.extend('useAutoSelectPos');
    /* UrlControl POS ID */
    if (posFrom.urlControl) {
      log('Using POS from UrlControl', posFrom.urlControl);
      return posFrom.urlControl;
    }
    if (posFrom.associatedPos) {
      log('Using POS from associated POS setting', posFrom.associatedPos);
      return posFrom.associatedPos;
    }
    if (posFrom.alreadySelected) {
      log('Using POS that is already selected', posFrom.alreadySelected);
      return posFrom.alreadySelected;
    }
    log('No valid autoselect targets found', { allOptions, allowedOptions });
    return undefined;
  }, [
    posFrom.urlControl,
    posFrom.associatedPos,
    posFrom.alreadySelected,
    allOptions,
    allowedOptions,
  ]);

  useEffect(() => {
    if (autoSelect) {
      onSelect(autoSelect.pointOfSaleID);
    }
  }, [autoSelect, onSelect]);
};

async function logoutAndReset(dispatch) {
  await dispatch(hardLogout());
  window.location.reload();
}
export default function PosSelect({ onCancel, toggleClockInOut, updateView }) {
  const dispatch = useDispatch();
  const onSelect = useCallback(id => dispatch(selectPos(id)), [dispatch]);

  const showAllRegisters = useSelector(getAllRegisters);
  const { t } = useTranslation('login');

  const selectedPOS = useSelector(getSelectedPos);

  const loadingDayOpen = useSelector(getIsLoading) && !!selectedPOS;

  const isFetchingWarehouses = useSelector(getIsFetchingWarehouses);
  const allowedWarehouses = useSelector(getAllowedWarehouses);
  const selectedWarehouse = useSelector(getSelectedWarehouse);
  const allowedWarehouseIDS = allowedWarehouses.map(wh => wh.warehouseID);
  const pointsByWarehouseIDS = useSelector(getEmployeePOSByWarehouseIDS);
  const pointsOfSale = useMemo(() => {
    if (selectedWarehouse) {
      return pointsByWarehouseIDS[selectedWarehouse.warehouseID] ?? [];
    }
    return allowedWarehouseIDS.flatMap(id => pointsByWarehouseIDS[id] || []);
  }, [selectedWarehouse, pointsByWarehouseIDS, allowedWarehouseIDS]);

  const filteredPointsOfSale = pointsOfSale?.filter(pos =>
    showAllRegisters ? true : !['ECOM'].includes(pos?.type),
  );

  useAutoSelectPos(onSelect, pointsOfSale, filteredPointsOfSale);

  const vatRatesFetching = useSelector(getVatRatesFetching);

  const isLoadingDayOpen = loadingDayOpen || (selectedPOS && vatRatesFetching);
  const loadingDayOpenLoaderText = t('loading.login', {
    context: 'checkingIfDayOpen',
  });
  const fetchingWarehousesLoaderText = t('loading.fetchingPointsOfSale');

  return (
    <Loader
      show={isLoadingDayOpen || isFetchingWarehouses}
      loadingText={
        <div style={{ padding: 4 }}>
          {isLoadingDayOpen
            ? loadingDayOpenLoaderText
            : fetchingWarehousesLoaderText}
          <Delay ms={8000}>
            <Button
              color="primary"
              variant="contained"
              onClick={() => dispatch(logoutAndReset)}
            >
              {t('loading.stuckInDayOpen.btn')}
            </Button>
          </Delay>
        </div>
      }
    >
      <Card data-testid="pos-select-container" style={styles.card}>
        <Card.Header style={styles.header}>
          <span data-testid="title">{t('selectPos.title')}</span>
          {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
          <span
            className="icon_close Clickable-icon"
            data-testid="close-icon"
            style={styles.close}
            onClick={() => {
              onCancel();
              toggleClockInOut(false);
              updateView('login');
            }}
          />
        </Card.Header>
        <Card.Body data-testid="body" style={styles.body}>
          <ListGroup style={{ border: 0 }}>
            {filteredPointsOfSale.map(pos => (
              <ListGroup.Item
                key={pos.pointOfSaleID}
                data-testid="pos-item"
                data-test-key={pos.pointOfSaleID}
                onClick={() => onSelect(pos.pointOfSaleID)}
                style={styles.pos}
                className="Clickable-div"
              >
                <span
                  style={{ fontSize: '24px' }}
                  data-testid="warehouse-name"
                  data-test-key={pos.warehouseName}
                >
                  {pos.warehouseName}
                </span>
                <br />
                <span data-testid="pos-name" data-test-key={pos.name}>
                  {pos.name}
                </span>
              </ListGroup.Item>
            ))}
          </ListGroup>
        </Card.Body>
      </Card>
    </Loader>
  );
}

PosSelect.propTypes = {
  onCancel: PropTypes.func.isRequired,
  toggleClockInOut: PropTypes.func.isRequired,
  updateView: PropTypes.func.isRequired,
};

function Delay({ children, ms }) {
  const [show, setShow] = React.useState(false);
  React.useEffect(() => {
    const t = setTimeout(() => {
      setShow(true);
    }, ms);
    return () => clearTimeout(t);
  }, [ms]);

  if (!show) {
    return null;
  }
  return children;
}
